import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getCommissionRules,
  getCommissionRulesByBusiness,
  getCommissionRule,
  createCommissionRule,
  updateCommissionRule,
  deleteCommissionRule,
  toggleRuleActive,
  getProjectTypes,
} from '@/services/commissionRules';
import type { Business, CreateCommissionRuleInput } from '@/types';

// ===========================================
// Query Keys
// ===========================================

export const ruleKeys = {
  all: ['commissionRules'] as const,
  lists: () => [...ruleKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...ruleKeys.lists(), filters] as const,
  byBusiness: (business: Business) => [...ruleKeys.lists(), { business }] as const,
  details: () => [...ruleKeys.all, 'detail'] as const,
  detail: (id: string) => [...ruleKeys.details(), id] as const,
  projectTypes: () => [...ruleKeys.all, 'projectTypes'] as const,
};

// ===========================================
// Commission Rule Hooks
// ===========================================

/**
 * Fetch all commission rules
 */
export function useCommissionRules() {
  return useQuery({
    queryKey: ruleKeys.lists(),
    queryFn: getCommissionRules,
  });
}

/**
 * Fetch commission rules for a specific business
 */
export function useCommissionRulesByBusiness(business: Business) {
  return useQuery({
    queryKey: ruleKeys.byBusiness(business),
    queryFn: () => getCommissionRulesByBusiness(business),
  });
}

/**
 * Fetch a single commission rule
 */
export function useCommissionRule(id: string | undefined) {
  return useQuery({
    queryKey: ruleKeys.detail(id!),
    queryFn: () => getCommissionRule(id!),
    enabled: !!id,
  });
}

/**
 * Fetch unique project types
 */
export function useProjectTypes() {
  return useQuery({
    queryKey: ruleKeys.projectTypes(),
    queryFn: getProjectTypes,
  });
}

/**
 * Create a new commission rule
 */
export function useCreateCommissionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCommissionRuleInput) => createCommissionRule(input),
    onSuccess: (rule) => {
      queryClient.invalidateQueries({ queryKey: ruleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ruleKeys.projectTypes() });
      toast.success(`Rule created for ${rule.projectType}`);
    },
    onError: (error) => {
      console.error('Create rule error:', error);
      toast.error('Failed to create rule');
    },
  });
}

/**
 * Update a commission rule
 */
export function useUpdateCommissionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<CreateCommissionRuleInput & { isActive: boolean }>;
    }) => updateCommissionRule(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ruleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ruleKeys.detail(id) });
      toast.success('Rule updated');
    },
    onError: (error) => {
      console.error('Update rule error:', error);
      toast.error('Failed to update rule');
    },
  });
}

/**
 * Toggle rule active status
 */
export function useToggleRuleActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      toggleRuleActive(id, isActive),
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ruleKeys.lists() });
      toast.success(isActive ? 'Rule activated' : 'Rule deactivated');
    },
    onError: (error) => {
      console.error('Toggle rule error:', error);
      toast.error('Failed to update rule status');
    },
  });
}

/**
 * Delete a commission rule
 */
export function useDeleteCommissionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCommissionRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ruleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ruleKeys.projectTypes() });
      toast.success('Rule deleted');
    },
    onError: (error) => {
      console.error('Delete rule error:', error);
      toast.error('Failed to delete rule');
    },
  });
}
